<script>
import { Helper } from '@/mixin/helper'
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import { DEPARTMENT_MUTATION_DELETE } from '@/services/api/setting/employee/department'
import { GET_UNIT } from '@/services/api/setting/employee/unit'
import {
  POSITION_MUTATION_UPDATE,
  POSITION_MUTATION_CREATE,
  GET_POSITION,
} from '@/services/api/setting/employee/position'

export default {
  components: { McGraphqlEditableTable },
  mixins: [Helper],
  props: {
    status: String,
  },
  data() {
    return {
      data: [],
      unitList: [],
    }
  },
  emits: ['refresh-component-unit'],
  computed: {
    fields() {
      return [
        {
          key: 'name',
          inputName: 'positionName',
          label: this.$t('setting.list.employeeSetting.position'),
          placeholder: this.$t('setting.list.employeeSetting.position'),
          thClass: 'mc-table--50',
          isSortable: true,
          editable: true,
          isEditable: true,
          validation: [['required']],
        },
        {
          name: 'unit',
          key: 'unitName',
          label: `${this.$t('setting.list.employeeSetting.workUnit')} - ${this.$t(
            'setting.list.employeeSetting.department'
          )}`,
          placeholder: this.$t('setting.list.employeeSetting.selectWorkUnit'),
          isEditable: true,
          type: 'select-resourceful',
          trackBy: 'structureId',
          optionsGetter: this.resourcefulQuery.unit,
          optionLabelFn: ({ item }) => item.name,
          getInitialObject: (item) => {
            return {
              structureId: item.unitId,
              name: item.unitName,
            }
          },
          customValueFn: (item) => item.unitId ?? 0,
          thStyle: 'width: 300px',
          validation: [['required']],
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_POSITION,
            variables: {
              filter: filter,
            },
          })
          const self = this
          self.data = res.data.positions.positions.data

          let result = res.data.positions.positions
          result.data.forEach((item, index) => {
            if (item.companyId == 1) {
              result.data[index].disableCheckbox = true
              result.data[index].isShowEdit = false
            } else if (item.hasRelation) {
              result.data[index].disableCheckbox = true
              result.data[index].isShowEdit = true
            }
            if (item.unitName) {
              result.data[index].unitName = `${item.unitName}`
              if (item.departmentName) {
                result.data[index].unitName += ` - ${item.departmentName}`
              }
            }
          })

          return result
        },
        store: async (data) => {
          const unitId = Number(data.unitName)
          const self = this
          const unitData = self.unitList.find((item) => item.structureId == unitId)
          const res = await this.$apollo.mutate({
            mutation: POSITION_MUTATION_CREATE,
            variables: {
              input: {
                name: data.positionName,
                unitName: unitData.name,
                departmentId: unitData.department.structureId,
              },
            },
          })
          if (res.data.createPosition.message.toLowerCase().includes('position name was exist')) {
            this.$mcToast.warning(this.$t('setting.list.employeeSetting.alert.positionExist'))
            return
          }
          res.data.createPosition.position.departmentId = unitData.department.structureId
          res.data.createPosition.position.departmentName = unitData.department.name
          res.data.createPosition.position.unitName = `${unitData.name}`
          if (unitData?.department && unitData.department.name != null) {
            res.data.createPosition.position.unitName += ` - ${unitData.department.name}`
          }
          res.data.createPosition.position.unitId = unitId
          self.data.push(res.data.createPosition.position)

          this.$emit('refresh-component-unit')
          return res.data.createPosition.position
        },
        update: async (id, oldData, newData) => {
          if (newData.positionName === undefined) newData.positionName = oldData.name
          if (newData.unitName === undefined) newData.unitName = oldData.unitId
          const self = this
          const unitId = Number(newData.unitName)
          const unitData = self.unitList.find((item) => item.structureId == unitId)
          const res = await this.$apollo.mutate({
            mutation: POSITION_MUTATION_UPDATE,
            variables: {
              input: {
                structureId: Number(id),
                name: newData.positionName,
                unitName: unitData.name,
                departmentId: unitData.department.structureId,
              },
            },
          })
          if (res.data.updatePosition.message.toLowerCase().includes('position name was exist')) {
            this.$mcToast.warning(this.$t('setting.list.employeeSetting.alert.positionExist'))
            return
          }
          const dataUpdateIndex = self.data.findIndex((item) => item.structureId == id)
          const updateData = res.data.updatePosition.position
          updateData.name = newData.positionName
          updateData.departmentId = unitData.department.structureId
          updateData.departmentName = unitData.department.name
          updateData.unitName = `${unitData.name}`
          if (unitData?.department && unitData.department.name != null) {
            updateData.unitName += ` - ${unitData.department.name}`
          }
          updateData.unitId = unitId
          updateData.hasRelation = oldData.hasRelation
          if (updateData.companyId == 1) {
            updateData.disableCheckbox = true
            updateData.isShowEdit = false
          } else if (updateData.hasRelation) {
            updateData.disableCheckbox = true
            updateData.isShowEdit = true
          }
          self.data[dataUpdateIndex] = updateData

          this.$emit('refresh-component-unit')
          return updateData
        },
        delete: async (id) => {
          const res = await this.$apollo.mutate({
            mutation: DEPARTMENT_MUTATION_DELETE,
            variables: {
              input: {
                structureId: Number(id),
              },
            },
          })

          return res.data.deleteCompanyStructure.isSuccessful
        },
      }
    },

    resourcefulQuery() {
      return {
        unit: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_UNIT,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.units.Units
          data.data.map((item, index) => {
            data.data[index].name = item.department
              ? `${item.name} - ${item.department.name}`
              : item.name
          })
          const result = {
            metadata: {
              total_page: data.pagination.total,
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },
      }
    },
  },
  mounted() {
    this.$store.commit('setting/SET_STATUS', this.status)
  },
  methods: {
    async onClickTab() {
      this.$store.commit('setting/SET_STATUS', this.status)
      const res = await this.$apollo.query({
        query: GET_UNIT,
        fetchPolicy: 'no-cache',
      })
      this.unitList = res.data.units.Units.data
    },
  },
}
</script>

<template>
  <!-- Position -->
  <b-tab class="border-0" @click="onClickTab">
    <template #title>
      <span class="d-sm-inline-block">{{ $t('setting.list.employeeSetting.position') }}</span>
    </template>

    <!-- Table -->
    <div class="table-responsive my-3">
      <McGraphqlEditableTable
        :fields="fields"
        :apiClient="query"
        trackBy="structureId"
        :searchable="true"
      />
    </div>
  </b-tab>
</template>
